import { useEffect, useState } from 'react';
import {
  Plus,
  Clock,
  LayoutTemplate,
  Sparkles,
  ArrowRight,
  FileImage,
  Folder,
  FolderOpen,
  Layers,
  Download,
  Search,
  Trash2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { useStudioStore } from '../stores/studioStore';
import { ExportHistoryPanel } from '../components/editor/export/ExportHistoryPanel';
import { usePanelStore } from '../stores/panelStore';
import { supabase } from '../lib/supabase';
import type { Project } from '../types';

const PRESETS = [
  { key: 'hero-standard', label: 'Website Hero', width: 1512, height: 545, description: 'Standard website hero banner' },
  { key: 'email-header', label: 'Email Header', width: 600, height: 200, description: 'Email newsletter header' },
  { key: 'social-facebook', label: 'Facebook Cover', width: 1200, height: 630, description: 'Facebook page cover photo' },
  { key: 'social-instagram', label: 'Instagram Post', width: 1080, height: 1080, description: 'Square Instagram post' },
  { key: 'hero-wide', label: 'Wide Hero', width: 1600, height: 500, description: 'Full-width wide hero banner' },
  { key: 'hero-square', label: 'Square Hero', width: 800, height: 800, description: 'Square format hero graphic' },
  { key: 'display-leaderboard', label: 'Leaderboard Ad', width: 728, height: 90, description: 'Standard leaderboard display ad' },
  { key: 'display-rectangle', label: 'Medium Rectangle', width: 300, height: 250, description: 'Medium rectangle display ad' },
] as const;

export default function DashboardPage() {
  const {
    projects,
    projectsLoading,
    loadProjects,
    createProject,
    deleteProject,
    setActiveProject,
    loadFullProject,
    templates,
    exportJobs,
    loadExportJobs,
  } = useStudioStore();
  const setCanvasDimensions = useStudioStore((s) => s.setCanvasDimensions);
  const { setActiveSection } = usePanelStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [exportsProjectId, setExportsProjectId] = useState<string | null>(null);
  const [exportsCount, setExportsCount] = useState<number | null>(null);

  useEffect(() => {
    loadProjects();
    loadExportJobs();
  }, [loadProjects, loadExportJobs]);

  useEffect(() => {
    let cancelled = false;
    const fetchExportCount = async () => {
      const { count, error } = await supabase
        .from('cs_export_records')
        .select('id', { count: 'exact', head: true });
      if (cancelled) return;
      if (error) {
        console.error('[Dashboard] Error counting exports:', error);
        return;
      }
      setExportsCount(count ?? 0);
    };
    fetchExportCount();
    return () => {
      cancelled = true;
    };
  }, [exportJobs.length]);

  const handleCreateFromPreset = async (preset: typeof PRESETS[number]) => {
    const project = await createProject(
      `Untitled ${preset.label}`,
      preset.description,
      { width: preset.width, height: preset.height },
      preset.key
    );
    if (project) {
      setCanvasDimensions(preset.width, preset.height);
      setActiveProject(project);
    }
  };

  const handleOpenProject = async (project: Project) => {
    const full = await loadFullProject(project.id);
    if (!full) return;
    const state = full.canvas_state;
    if (state?.dimensions) {
      setCanvasDimensions(state.dimensions.width, state.dimensions.height);
    }
    setActiveProject(full);
  };

  const handleDeleteProject = async (id: string) => {
    await deleteProject(id);
    setDeleteConfirmId(null);
  };

  const filteredProjects = searchQuery.trim()
    ? projects.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.status?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : projects;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-lg border border-red-400/60 bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 shadow-[0_0_20px_rgba(239,68,68,0.25)]"
        >
          <AlertTriangle size={20} className="text-white shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-white uppercase tracking-wide">
              Hard refresh before you begin
            </p>
            <p className="text-xs text-red-50/95 mt-1 leading-relaxed">
              Creator Studio ships updates often. Every time you open this app,
              hard refresh (Cmd + Shift + R on Mac, Ctrl + Shift + R on Windows)
              or clear your browser cache to make sure you’re on the latest
              build. If something looks broken, refresh first.
            </p>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-100 tracking-tight">
            Creator Studio
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Design once. Brand everywhere.
          </p>
        </div>

        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-cyan-400" />
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Quick Start</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                onClick={() => handleCreateFromPreset(preset)}
                className="group relative bg-white/[0.02] border border-white/10 rounded-xl p-4 text-left hover:bg-white/[0.04] hover:border-cyan-500/30 transition-all duration-200"
              >
                <div
                  className="w-full bg-white/5 rounded-lg mb-3 flex items-center justify-center border border-white/5 overflow-hidden"
                  style={{
                    aspectRatio: `${preset.width} / ${preset.height}`,
                    maxHeight: '80px',
                  }}
                >
                  <LayoutTemplate size={16} className="text-gray-600" />
                </div>
                <h3 className="text-xs font-medium text-gray-300 group-hover:text-gray-100 transition-colors">
                  {preset.label}
                </h3>
                <p className="text-[10px] text-gray-600 mt-0.5">
                  {preset.width} x {preset.height}
                </p>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Plus size={14} className="text-cyan-400" />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Recent Projects</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects..."
                  className="pl-8 pr-7 py-1.5 bg-white/[0.03] border border-white/[0.08] rounded-lg text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 w-48 transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              {projects.length > 0 && (
                <button
                  onClick={() => setActiveSection('projects')}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  View All <ArrowRight size={12} />
                </button>
              )}
            </div>
          </div>

          {projectsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-8 text-center">
              <FileImage size={32} className="text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-1">
                {searchQuery ? 'No matching projects' : 'No projects yet'}
              </p>
              <p className="text-xs text-gray-600">
                {searchQuery
                  ? 'Try a different search term'
                  : 'Choose a preset above to create your first hero banner'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {filteredProjects.slice(0, 6).map((project) => (
                <div
                  key={project.id}
                  className="group relative bg-white/[0.02] border border-white/10 rounded-xl transition-all duration-200 hover:bg-white/[0.04] hover:border-cyan-500/30"
                >
                  <button
                    onClick={() => handleOpenProject(project)}
                    className="w-full p-4 text-left"
                  >
                    <div className="w-full aspect-video bg-white/5 rounded-lg mb-3 flex items-center justify-center border border-white/5 overflow-hidden">
                      {project.thumbnail_url ? (
                        <img
                          key={project.thumbnail_url}
                          src={project.thumbnail_url}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Folder size={20} className="text-gray-600" />
                      )}
                    </div>
                    <h3 className="text-xs font-medium text-gray-300 group-hover:text-gray-100 transition-colors truncate">
                      {project.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider ${
                        project.status === 'draft'
                          ? 'bg-gray-500/20 text-gray-400'
                          : project.status === 'in_progress'
                            ? 'bg-amber-500/20 text-amber-400'
                            : project.status === 'approved'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {project.status?.replace('_', ' ')}
                      </span>
                      <span className="text-[10px] text-gray-600">
                        {project.updated_at
                          ? new Date(project.updated_at).toLocaleDateString()
                          : ''}
                      </span>
                    </div>
                  </button>

                  {deleteConfirmId === project.id ? (
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-[#1a1a1a] border border-red-500/30 rounded-lg p-1.5 shadow-xl animate-fade-in z-10">
                      <span className="text-[10px] text-red-400 px-1">Delete?</span>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px] font-medium hover:bg-red-500/30 transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-2 py-0.5 rounded bg-white/[0.06] text-zinc-400 text-[10px] font-medium hover:bg-white/10 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExportsProjectId(
                            exportsProjectId === project.id ? null : project.id
                          );
                        }}
                        className={`p-1.5 rounded-md bg-black/40 transition-colors ${
                          exportsProjectId === project.id
                            ? 'text-purple-400 bg-purple-500/20'
                            : 'text-zinc-500 hover:text-purple-400 hover:bg-purple-500/10'
                        }`}
                        title="View exports"
                      >
                        <Download size={13} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(project.id)}
                        className="p-1.5 rounded-md bg-black/40 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete project"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {exportsProjectId && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Download size={16} className="text-purple-400" />
                <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                  Recent Exports
                </h2>
              </div>
              <button
                onClick={() => setExportsProjectId(null)}
                className="p-1 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
              >
                <X size={14} />
              </button>
            </div>
            <ExportHistoryPanel
              projectId={exportsProjectId}
              compact={true}
              maxItems={3}
            />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <FolderOpen size={14} className="text-emerald-400" />
              <span className="text-xs text-gray-500">Projects</span>
            </div>
            <p className="text-xl font-semibold text-gray-200">{projects.length}</p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <Layers size={14} className="text-blue-400" />
              <span className="text-xs text-gray-500">Templates</span>
            </div>
            <p className="text-xl font-semibold text-gray-200">{templates.length}</p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-2 mb-1">
              <Download size={14} className="text-cyan-400" />
              <span className="text-xs text-gray-500">Exports</span>
            </div>
            <p className="text-xl font-semibold text-gray-200">
              {exportsCount !== null ? exportsCount : exportJobs.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
